import { AxiosError } from "axios";

/**
 * Human-readable copy for the BE error codes the API throws as the
 * `message` field (NestJS `*Exception(CODE)`). Keyed by the raw code
 * so the FE never surfaces a bare `TASK_DEFINITION_FROZEN` or the
 * useless `Request failed with status code 409` to an organiser.
 *
 * Add a row here whenever the BE introduces a new coded error that a
 * user can trigger — keep the copy actionable (tell them what to do),
 * not just descriptive.
 */
export const FRIENDLY_MESSAGE_BY_CODE: Record<string, string> = {
  // ─── task definition / editing ──────────────────────────────────
  TASK_DEFINITION_FROZEN:
    "This task already has submitted responses, so its response type can no longer be changed. You can still edit the title, description, due date and other settings.",
  TASK_LIBRARY_DEFINITION_FROZEN:
    "This task already has responses, so its response type, answer options, conditional tasks and table structure can no longer be changed. You can still edit the title, description, due date and other settings.",
  CONDITIONAL_CHILD_HAS_SUBMITTED_RESPONSES:
    "A conditional task attached to one of the options already has submitted answers, so it can't be detached. Reopen the option's conditional tasks and keep it selected.",
  LIBRARY_TASK_REQUIRES_BUILD_TYPE:
    "There are no build types in the Library yet. Add a build type in Libraries > Build Types, then add this task to the Library.",
  LIBRARY_TASK_ARCHIVED:
    "This task has been archived in the Library, so it can't be added to the Show. The list has been refreshed — select the tasks again.",
  TASK_NOT_FOUND: "This task no longer exists. It may have been deleted.",
  TASK_INSTANCE_NOT_FOUND:
    "We couldn't find this task for the selected stand. Try reopening the task.",
  TASK_PAIR_NOT_FOUND:
    "This exhibitor is no longer linked to the stand. Refresh and try again.",
  STAND_NOT_OWNED_BY_PAIR:
    "This stand isn't assigned to the selected exhibitor anymore.",
  EXHIBITOR_PAIR_NOT_FOUND:
    "An exhibitor no longer exists. It may have been deleted. Refresh and try again.",

  // ─── approvals / reviewers ──────────────────────────────────────
  TASK_REVIEWER_NOT_FOUND: "This reviewer is no longer assigned to the task.",
  TASK_REVIEW_KEY_INVALID:
    "This review link is invalid. Please request a fresh one.",
  TASK_REVIEW_KEY_EXPIRED:
    "This review link has expired. Please request a new one.",
  TASK_REVIEW_KEY_USED: "This review link has already been used.",

  // ─── certificates ───────────────────────────────────────────────
  TASK_CERTIFICATE_NOT_FOUND: "This certificate template no longer exists.",

  // ─── paid features / custom emails ──────────────────────────────
  FEATURE_NOT_ENABLED:
    "This feature isn't activated for this show. Activate it and try again.",
  CUSTOM_EMAIL_TEMPLATE_NOT_FOUND:
    "This template no longer exists. It may have been deleted.",

  // ─── two-factor / SMS ───────────────────────────────────────────
  SMS_TWO_FACTOR_REGION_NOT_SUPPORTED:
    "We can't send verification codes to this country yet. Try a phone number from another country, or set up another verification option.",
  TWO_FACTOR_TOO_MANY_ATTEMPTS:
    "You've asked for codes too often. Wait for the timer on the resend button before trying again.",
  SMS_TWO_FACTOR_DAILY_LIMIT_REACHED:
    "You've reached the daily limit for verification codes. Please try again tomorrow.",
  // Deliberately says nothing about which country tripped the guard — the BE
  // keeps that vague on purpose so the message can't be used to probe which
  // destinations are still open.
  SMS_TWO_FACTOR_TEMPORARILY_UNAVAILABLE:
    "SMS verification is temporarily unavailable. Please try again later, or use another verification method.",

  // ─── app version ────────────────────────────────────────────────
  NEW_VERSION_MUST_BE_GREATER_THAN_OLD:
    "The new version should be higher than the current one. Another admin may have just raised it — reopen the dialog to see the latest.",

  // ─── user invites ───────────────────────────────────────────────
  EMAIL_USED_FOR_ANOTHER_ROLE: "This email is already used for another role.",
  ORGANISER_ALREADY_ASSIGNED_TO_SHOW:
    "This organiser already has access to this show.",
};

/**
 * Generic fallbacks per HTTP status — used when the BE didn't send a
 * recognised coded message. Anything is better than echoing
 * "Request failed with status code N".
 */
const FALLBACK_BY_STATUS: Record<number, string> = {
  400: "Some of the entered information isn't valid. Please review and try again.",
  401: "Your session has expired. Please sign in again.",
  403: "You don't have permission to do that.",
  404: "We couldn't find what you were looking for. It may have been removed.",
  409: "This action conflicts with the current state. Refresh and try again.",
  413: "The uploaded file is too large.",
  422: "Some of the entered information isn't valid. Please review and try again.",
  429: "Too many requests — please wait a moment and try again.",
  500: "Something went wrong on our side. Please try again shortly.",
  502: "The service is temporarily unavailable. Please try again shortly.",
  503: "The service is temporarily unavailable. Please try again shortly.",
  504: "The request timed out. Please try again.",
};

const GENERIC_FALLBACK = "Something went wrong. Please try again.";

/** True when the string looks like a raw BE code (UPPER_SNAKE_CASE). */
const looksLikeCode = (value: string): boolean => /^[A-Z0-9_]+$/.test(value);

/**
 * Extracts a user-friendly message from any error (especially Axios).
 *
 * Resolution order:
 *   1. Known BE code → curated copy (`FRIENDLY_MESSAGE_BY_CODE`).
 *   2. A human-sentence BE message (not an UPPER_SNAKE code) → used as-is.
 *   3. HTTP status → generic per-status fallback.
 *   4. Generic fallback — never the raw "Request failed with status code N".
 */
/**
 * Every message the BE sent, not just the first.
 *
 * Endpoints that validate a whole batch (product bulk upload) answer with one
 * entry per offending row — collapsing that to `[0]` would make the supplier
 * re-submit once per broken row to discover them one at a time. Falls back to
 * the single resolved message for every other error shape, so callers can
 * always render the result as a list.
 */
export const getErrorMessages = (error: unknown): string[] => {
  const isAxios =
    !!error &&
    typeof error === "object" &&
    "isAxiosError" in error &&
    (error as { isAxiosError?: boolean }).isAxiosError;

  if (isAxios) {
    const rawMessage = (error as AxiosError<{ message?: string | string[] }>)
      .response?.data?.message;

    if (Array.isArray(rawMessage)) {
      // Bare codes are dropped rather than shown raw — same rule as
      // getErrorMessage. If that empties the list, fall through below.
      const messages = rawMessage.filter(
        (message) => !!message && !looksLikeCode(message)
      );

      if (messages.length) return messages;
    }
  }

  return [getErrorMessage(error)];
};

export const getErrorMessage = (error: unknown): string => {
  const isAxios =
    !!error &&
    typeof error === "object" &&
    "isAxiosError" in error &&
    (error as { isAxiosError?: boolean }).isAxiosError;

  if (isAxios) {
    const axiosErr = error as AxiosError<{ message?: string | string[] }>;
    const rawMessage = axiosErr.response?.data?.message;
    // class-validator returns `message` as a string[] — take the first.
    const beMessage = Array.isArray(rawMessage) ? rawMessage[0] : rawMessage;

    if (beMessage) {
      const friendly = FRIENDLY_MESSAGE_BY_CODE[beMessage];
      if (friendly) return friendly;
      // A real sentence from the BE (validation message, custom text)
      // is already user-facing — surface it. Bare codes fall through
      // to the status fallback so we don't leak `SOME_RAW_CODE`.
      if (!looksLikeCode(beMessage)) return beMessage;
    }

    const status = axiosErr.response?.status;
    if (status && FALLBACK_BY_STATUS[status]) {
      return FALLBACK_BY_STATUS[status];
    }
    return GENERIC_FALLBACK;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return GENERIC_FALLBACK;
};

/**
 * How long the server says to wait before retrying, in seconds.
 *
 * Only meaningful on a 429. The SMS limits are the reason this exists: the
 * resend cooldown escalates 60s → 5m → 30m → 24h, so a hardcoded client-side
 * timer re-enables the button while the server is still refusing and walks the
 * user into an error they could not have predicted.
 *
 * Reads the body before the header. `Retry-After` is the standard and the API
 * does send it, but a browser can only see a response header the server has
 * listed in `Access-Control-Expose-Headers` — and that is one more thing to go
 * wrong across origins than a field in JSON we already parse.
 *
 * Returns null when the error carries no wait, so callers can tell "no answer"
 * apart from "retry immediately".
 */
export const getRetryAfterSeconds = (error: unknown): number | null => {
  const axiosErr = error as AxiosError<{ retryAfterSeconds?: number }>;

  if (!axiosErr?.response) return null;

  const fromBody = axiosErr.response.data?.retryAfterSeconds;
  if (typeof fromBody === "number" && fromBody > 0) return Math.ceil(fromBody);

  const header = axiosErr.response.headers?.["retry-after"];
  const fromHeader = Number(header);

  return Number.isFinite(fromHeader) && fromHeader > 0
    ? Math.ceil(fromHeader)
    : null;
};

/**
 * Statuses the public embedded widget (`embedded-shows/*`) answers on purpose:
 * 403 / 404 for a disabled list, bad token or unlisted show, 429 when rate
 * limited. The widget renders its own copy for each.
 */
const EXPECTED_EMBEDDED_SHOWS_STATUSES = [403, 404, 429];

/**
 * True for an expected rejection from the public embedded widget's API — not
 * an access problem worth a snackbar, nor an API fault worth reporting.
 */
export const isExpectedEmbeddedShowsRejection = (error: unknown): boolean => {
  const axiosErr = error as AxiosError | undefined;
  const status = axiosErr?.response?.status;
  const url = axiosErr?.config?.url ?? "";

  return (
    !!status &&
    EXPECTED_EMBEDDED_SHOWS_STATUSES.includes(status) &&
    /^\/?embedded-shows(\/|\?|$)/.test(url)
  );
};

/**
 * Extracts the raw BE error code (the UPPER_SNAKE `message` a NestJS
 * `*Exception(CODE)` throws) for control-flow branching — distinct from
 * `getErrorMessage`, which turns it into user copy. Returns null when the
 * error carries no recognisable code.
 */
export const getErrorCode = (error: unknown): string | null => {
  const isAxios =
    !!error &&
    typeof error === "object" &&
    "isAxiosError" in error &&
    (error as { isAxiosError?: boolean }).isAxiosError;

  if (!isAxios) return null;

  const axiosErr = error as AxiosError<{ message?: string | string[] }>;
  const rawMessage = axiosErr.response?.data?.message;
  const beMessage = Array.isArray(rawMessage) ? rawMessage[0] : rawMessage;

  return beMessage && looksLikeCode(beMessage) ? beMessage : null;
};
